'use client';
import React, { useEffect, useRef } from 'react';

const timeline = [
  {
    period: 'Early Career',
    role: 'Human Resources Professional',
    detail: 'Built a foundation in HR and talent acquisition, developing deep insight into what employers look for in candidates.',
  },
  {
    period: 'Mid Career',
    role: 'Senior Career Consultant',
    detail: 'Worked with a leading career services firm, coaching professionals across industries on job search strategy and personal branding.',
  },
  {
    period: '10+ Years Ago',
    role: 'Certified Professional Résumé Writer',
    detail: 'Earned professional certification in résumé writing and began building a reputation for results-driven career documents.',
  },
  {
    period: 'Present',
    role: 'Founder, Betty Berkely Career Coaching',
    detail: 'Leading a dedicated team of career professionals helping job seekers at every level land meaningful roles with confidence.',
  },
];

const audiences = [
  {
    title: 'Recent Graduates & Early-Career Professionals',
    desc: 'First-time job seekers and new graduates who need a strong foundation: a compelling résumé and a clear job search strategy.',
  },
  {
    title: 'Mid-Career Professionals',
    desc: 'Experienced professionals looking to advance, change roles, or re-enter the workforce with a refreshed, competitive profile.',
  },
  {
    title: 'Career Changers & Industry Transitioners',
    desc: 'Professionals pivoting to new fields who need their transferable skills articulated clearly and persuasively.',
  },
  {
    title: 'Senior Executives & Leadership Candidates',
    desc: 'Senior professionals targeting leadership roles who require executive-level résumés and strategic career positioning.',
  },
];

export default function CareerHistory() {
  const timelineRef = useRef<HTMLDivElement>(null);
  const audienceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    if (timelineRef?.current) observer?.observe(timelineRef?.current);
    if (audienceRef?.current) observer?.observe(audienceRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      {/* Career History */}
      <section className="section-plum relative py-24 lg:py-32 overflow-hidden" id="history">
        <div className="noise-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div ref={timelineRef} className="reveal-section">
            <span className="section-index block mb-4" style={{ color: 'rgba(201,168,76,0.5)' }}>
              BETTY'S JOURNEY
            </span>
            <h2
              className="font-fraunces font-medium mb-14"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: '#F5F0E8', lineHeight: '1.1' }}
            >
              A Career Built on Helping Others Succeed
            </h2>

            <div className="relative">
              {/* Vertical line */}
              <div
                className="absolute left-[7.5rem] top-0 bottom-0 w-px hidden md:block"
                style={{ background: 'rgba(201,168,76,0.2)' }}
              />

              <div className="flex flex-col gap-10">
                {timeline?.map((item, i) => (
                  <div key={i} className="flex flex-col md:flex-row gap-4 md:gap-8 items-start">
                    <div
                      className="flex-shrink-0 font-mono-custom text-xs tracking-wide md:text-right md:w-28"
                      style={{ color: 'rgba(201,168,76,0.6)', paddingTop: '3px' }}
                    >
                      {item?.period}
                    </div>
                    <div className="relative md:pl-8">
                      {/* Dot */}
                      <div
                        className="absolute left-0 top-1.5 w-3 h-3 rounded-full hidden md:block"
                        style={{ background: '#C9A84C', transform: 'translateX(-50%)', marginLeft: '-1px' }}
                      />
                      <h3 className="font-semibold text-base mb-1" style={{ color: '#F5F0E8' }}>{item?.role}</h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'rgba(245,240,232,0.55)' }}>{item?.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="section-cream relative py-24 lg:py-32 overflow-hidden" id="who-we-serve">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div ref={audienceRef} className="reveal-section mb-14">
            <span className="section-index block mb-4" style={{ color: 'rgba(74,25,66,0.4)' }}>
              WHO WE SERVE
            </span>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <h2
                className="font-fraunces font-medium"
                style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: '#4A1942', lineHeight: '1.1' }}
              >
                Built for Every Job Seeker
              </h2>
              <p className="max-w-sm text-base leading-relaxed" style={{ color: '#4A4A4A' }}>
                Whether you're just starting out or stepping into a senior role, Betty's team has the expertise to help you succeed.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {audiences?.map((audience, i) => (
              <div
                key={i}
                className="process-card-light rounded-sm p-8 transition-all duration-300"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <h3 className="font-semibold text-base mb-3" style={{ color: '#4A1942' }}>{audience?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6A6A6A' }}>{audience?.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 text-center">
            <a href="/home#contact" className="cta-gold px-10 py-4 rounded-sm text-sm font-semibold tracking-wide inline-flex items-center gap-3">
              Start Your Career Journey Today
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
